import { useMutation, useQueryClient } from '@tanstack/react-query';
import API, { getCompressedFile } from '../../../../context/API';
import { requireActiveStoreId } from '../../../../utils/activeStore';
import { notification } from '../../../../commonComponents/Notification/Notification';
import { invalidateCategoryCache, updateCategorySequence } from '../../../../APIs/menuAPI';
import { Category, CategoryItem } from '../types';
import { QUERY_KEYS } from '../constants';

interface SubmitCategoryPayload {
  categoryName: string;
  sequenceNumber: number | string;
  imageFile: File | null;
  existingPicture: string;
  selectedItems: CategoryItem[];
  editingCategory: Category | null;
}

async function uploadCategoryImage(imageFile: File): Promise<string> {
  const compressed = await getCompressedFile(imageFile);
  const formData = new FormData();
  formData.append('file', compressed);
  formData.append('type', 'kiwikiosk_categories');
  const response = await API.post('/images/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data.url;
}

export function useSubmitCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      categoryName,
      sequenceNumber,
      imageFile,
      existingPicture,
      selectedItems,
      editingCategory,
    }: SubmitCategoryPayload) => {
      // Fail fast on a missing store before doing any work (image upload
      // included). storeId is required in the body on create; updates don't
      // accept it in the body, but we still confirm a store is active.
      const storeId = requireActiveStoreId();

      const itemsArray = selectedItems
        .map((item) => item.id)
        .filter((id) => id != null)
        .map((id) => parseInt(String(id), 10));

      // Upload the image separately first (same two-step flow as before), then
      // send category data with the resulting path.
      let picturePath = existingPicture;
      if (imageFile) {
        picturePath = await uploadCategoryImage(imageFile);
      }

      const requestData = {
        category: categoryName,
        sequence_number: sequenceNumber,
        items: itemsArray,
        picture: picturePath,
      };

      if (editingCategory) {
        // storeId is not accepted on category updates.
        const response = await API.put(`/categories/${editingCategory.id}`, requestData);
        return response.data.data;
      }

      const response = await API.post('/categories', { ...requestData, storeId });
      return response.data.data;
    },
    onSuccess: (_, { editingCategory }) => {
      // Keep the legacy menuAPI cache (used by the Overview tab) in sync too.
      invalidateCategoryCache();
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CATEGORIES] });
      notification.success(
        editingCategory ? 'Category updated' : 'Category created',
        `The category was successfully ${editingCategory ? 'updated' : 'created'}.`,
        5
      );
    },
    onError: (error: any) => {
      const msg = error.response?.data?.message || error.message || 'Unable to save the category.';
      notification.error('An error occurred', msg, 5);
    },
  });
}

export function useDeleteCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number | string) => API.delete(`/categories/${id}`),
    onSuccess: () => {
      invalidateCategoryCache();
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CATEGORIES] });
      notification.success('Category deleted', 'The category was successfully deleted.', 5);
    },
    onError: (error: any) => {
      const msg = error.response?.data?.message || error.message || 'Unable to delete the category.';
      notification.error('An error occurred', msg, 5);
    },
  });
}

export function useReorderCategories() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (reorderedData: { id: number; sequence_number: number }[]) =>
      updateCategorySequence(reorderedData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CATEGORIES] });
      notification.success('Categories reordered', 'The new order has been saved.', 3);
    },
    onError: (error: any) => {
      const msg = error.response?.data?.message || error.message || 'Unable to update the category order.';
      notification.error('Error updating order', msg, 5);
    },
  });
}
